/**
 * Wizard footer — Back on the left, the step's forward actions on the right,
 * and exactly ONE helper line under the primary.
 *
 * A disabled control always NAMES its reason (strategy-blocks designer
 * invariant 14): `disabledReason` both gates the buttons and renders the single
 * `formDisabledReason` line. «Create with AI» is contained ONLY on the review
 * step (owner ruling 2026-09-10).
 */

import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { STRATEGY_LIST } from '@test-kit/selectors/strategy-list';

/** Copy shown under «Create with AI» whenever the launch is not blocked. */
export const WIZARD_LAUNCH_REASSURANCE =
  'Runs in the background · you can close this dialog · progress in Tasks.';

export type WizardFooterProps =
  | {
    kind: 'steps';
    onBack?: () => void;
    onSkipToReview: () => void;
    onNext: () => void;
    nextLabel: string;
    disabledReason?: string;
  }
  | {
    kind: 'review';
    /**
     * Omitted by QUICK create (Task B7): the quick screen is not a step, so it
     * has nothing to go back to — the footer then renders no Back at all.
     */
    onBack?: () => void;
    onSaveWithoutGenerating: () => void;
    onCreateWithAI: () => void;
    submitting: boolean;
    disabledReason?: string;
    /**
     * How the one reason line reads. `error` (default) is a real blocker —
     * something is wrong with the request. `neutral` is a WAITING state, e.g.
     * quick create's «Loading insight analyses…»: the primary is honestly
     * disabled and the reason is named, but nothing has failed, so painting it
     * red would be the red-on-hydration chip invariant 5 forbids (Task B9d fix
     * round 1).
     */
    disabledReasonTone?: 'neutral' | 'error';
    describedBy?: string;
    /**
     * Ruling FW9-R5: «Save without generating» is gated TOO, because the launch
     * surface's document already exists and saving again would create a second
     * strategy. It is a separate flag on purpose: `disabledReason` alone must
     * never reach this button — a missing name or a library conflict is a reason
     * not to GENERATE, not a reason not to save.
     */
    saveWithoutGeneratingBlocked?: boolean;
  }
  | {
    kind: 'fromQuick';
    onBackToQuick: () => void;
    onDone: () => void;
  };

/**
 * `gated` carries the wizard-level "step 1 is not complete yet" state, so the
 * dialog does not have to repeat it in every footer packet.
 */
export type WizardFooterComponentProps = WizardFooterProps & { gated?: boolean };

const BackButton: React.FC<{ onClick: () => void; label?: string }> = ({ onClick, label = 'Back' }) => (
  <Button variant="outlined" onClick={onClick} data-testid={STRATEGY_LIST.wizardBack}>
    {label}
  </Button>
);

export const WizardFooter: React.FC<WizardFooterComponentProps> = (props) => {
  let left: React.ReactNode = <span />;
  let right: React.ReactNode = null;
  let helper: React.ReactNode = null;

  if (props.kind === 'steps') {
    const blocked = Boolean(props.gated) || Boolean(props.disabledReason);
    if (props.onBack) left = <BackButton onClick={props.onBack} />;
    right = (
      <>
        <Button
          variant="outlined"
          onClick={props.onSkipToReview}
          disabled={blocked}
          title={props.disabledReason}
          data-testid={STRATEGY_LIST.wizardSkipToReview}
        >
          Skip to review
        </Button>
        <Button
          variant="contained"
          onClick={props.onNext}
          disabled={blocked}
          title={props.disabledReason}
          data-testid={STRATEGY_LIST.wizardNext}
        >
          {props.nextLabel}
        </Button>
      </>
    );
    if (props.disabledReason) {
      helper = (
        <Typography variant="caption" color="text.secondary" data-testid={STRATEGY_LIST.formDisabledReason}>
          {props.disabledReason}
        </Typography>
      );
    }
  } else if (props.kind === 'review') {
    const blocked = props.submitting || Boolean(props.disabledReason);
    if (props.onBack) left = <BackButton onClick={props.onBack} />;
    right = (
      <>
        <Button
          variant="outlined"
          onClick={props.onSaveWithoutGenerating}
          disabled={props.submitting || Boolean(props.saveWithoutGeneratingBlocked)}
          {...(props.saveWithoutGeneratingBlocked && props.disabledReason
            ? { title: props.disabledReason }
            : {})}
          data-testid={STRATEGY_LIST.formSaveWithoutAIBtn}
        >
          {props.submitting ? 'Saving…' : 'Save without generating'}
        </Button>
        <Button
          variant="contained"
          onClick={props.onCreateWithAI}
          disabled={blocked}
          {...(props.disabledReason
            ? { 'aria-disabled': true, title: props.disabledReason, 'aria-describedby': props.describedBy }
            : {})}
          data-testid={STRATEGY_LIST.formSaveBtn}
        >
          {props.submitting ? 'Saving…' : 'Create with AI'}
        </Button>
      </>
    );
    helper = props.disabledReason
      ? (
        <Typography
          variant="caption"
          color={props.disabledReasonTone === 'neutral' ? 'text.secondary' : 'error.main'}
          data-testid={STRATEGY_LIST.formDisabledReason}
        >
          {props.disabledReason}
        </Typography>
      )
      : (
        <Typography variant="caption" color="text.secondary">
          {WIZARD_LAUNCH_REASSURANCE}
        </Typography>
      );
  } else {
    left = <BackButton onClick={props.onBackToQuick} label="Back to quick create" />;
    right = (
      <Button variant="contained" onClick={props.onDone} data-testid={STRATEGY_LIST.wizardNext}>
        Done
      </Button>
    );
  }

  return (
    <Box
      data-testid={STRATEGY_LIST.wizardFooter}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1,
        pt: 2,
        pb: 2,
        mt: 3,
        borderTop: 1,
        borderColor: 'divider',
        // FP-6 (Task B9d must-fix 1): the primary, the ONE disabled reason and
        // the reassurance line stay pinned at the dialog bottom — only the step
        // body scrolls. Ruling R7 keeps the bar full width BELOW the 800px
        // column, so it is not moved into the `SDialog` actions slot.
        position: 'sticky',
        bottom: 0,
        zIndex: 3,
        backgroundColor: 'background.default',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1 }}>{left}</Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75 }}>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>{right}</Box>
        {helper}
      </Box>
    </Box>
  );
};

WizardFooter.displayName = 'WizardFooter';
